import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { notification } from 'antd'; // Add this import
import DefaultOrder from './DefaultOrder';
import propertiesService from '../services/propertiesService';
import eventBus from '../utils/eventBus'; // Add this import

export const normalizeCartItemsForEditing = (items = {}) => Object.fromEntries(
  Object.entries(items || {}).map(([key, item = {}]) => {
    const type = String(item.type || '').toLowerCase();
    const isExtra = item.isExtra === true || item.isExtra === 'true' || type === 'extra';

    return [key, {
      ...item,
      id: item.id ?? key,
      price: parseFloat(item.price) || 0,
      type: isExtra ? 'extra' : (item.type || 'product'),
      qty: parseFloat(item.qty ?? item.quantity) || 0,
      name: item.name,
      ...(isExtra ? { isExtra: true } : {}),
    }];
  }),
);

const EditCartModal = ({
    isOpen,
    onClose,
    propertyId,
    categories,
    products,
    packs,
    defaultOrder,
    onUpdate
  }) => {
    const [formData, setFormData] = useState({
      property_id: '',
      property_title: '',
      property_type: '',
      property_description: '',
      contact_name: '',
      cleaner_information: '',
      contact_number: '',
      contact_description: '',
      arrival_times: ['', ''],
      completion_times: ['', ''],
      date: '',
      quantity: {},
      appartment_address: '',
      appartment_number: '',
      delivery_city: '',
      delivery_country: '',
      post_code: '',
      product_quantity: {},
    });
  
    useEffect(() => {
      if (defaultOrder) {
        setFormData(prev => ({
          ...prev,
          product_quantity: normalizeCartItemsForEditing(defaultOrder)
        }));
      }
    }, [defaultOrder]);
  
    useEffect(() => {
      if (propertyId) {
        fetchPropertyData();
      }
    }, [propertyId]);
  
    const fetchPropertyData = async () => {
      try {
        const response = await propertiesService.getPropertyById(propertyId);
        if (response && response.property) {
          const property = response.property;
          const propertyProductQuantity = JSON.parse(property.product_quantity);
  
          const currentCartItems = normalizeCartItemsForEditing(defaultOrder);
  
          const mergedProductQuantity = {
            ...propertyProductQuantity,
            ...currentCartItems
          };
  
          setFormData({
            property_id: property.id.toString(),
            property_title: property.property_title,
            property_type: property.property_type_id.toString(),
            property_description: property.property_description,
            contact_name: property.contact_person,
            cleaner_information: property.cleaner_information,
            contact_number: property.contact_number,
            contact_description: property.contact_description || "",
            arrival_times: property.arrival_time
              ? property.arrival_time.split(":")
              : ["", ""],
            completion_times: property.completion_time
              ? property.completion_time.split(":")
              : ["", ""],
            date: property.date,
            quantity: JSON.parse(property.quantity),
            appartment_address: property.apartment_address,
            appartment_number: property.apartment_number,
            delivery_city: property.delivery_city,
            delivery_country: property.delivery_country,
            post_code: property.delivery_zip_code,
            product_quantity: mergedProductQuantity,
          });
        }
      } catch (error) {
        console.error("Error fetching property data:", error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch property data'
        });
      }
    };
  
    const handleQuantityChange = (productId, change, isProduct, price) => {
        setFormData(prevData => {
          const productIdStr = productId.toString();
          const currentQuantity = parseInt(prevData.product_quantity[productIdStr]?.qty || 0);
          const newQuantity = Math.max(0, currentQuantity + change);
      
          const productName = isProduct 
            ? products[Object.keys(products)[0]]?.find(p => p.id.toString() === productIdStr)?.product_title
            : packs?.find(p => p.id.toString() === productIdStr)?.name;
      
          return {
            ...prevData,
            product_quantity: {
              ...prevData.product_quantity,
              [productIdStr]: {
                id: productIdStr,
                price: parseFloat(price),
                type: isProduct ? "product" : "pack",
                qty: newQuantity,
                name: productName || prevData.product_quantity[productIdStr]?.name
              }
            }
          };
        });
      };
  
    const handleRentTypeChange = (productId, rentType) => {
      setFormData(prevData => ({
        ...prevData,
        product_quantity: {
          ...prevData.product_quantity,
          [productId]: {
            ...prevData.product_quantity[productId],
            rentType
          }
        }
      }));
    };
  
    const prepareFormData = (type) => {
      const dataToSend = new FormData();
      dataToSend.append("type", type);
  
      Object.entries(formData).forEach(([key, value]) => {
        if (!['quantity', 'product_quantity', 'arrival_times', 'completion_times'].includes(key)) {
          dataToSend.append(key, value);
        }
      });
  
      formData.arrival_times.forEach((time) => dataToSend.append("arrival_times[]", time));
      formData.completion_times.forEach((time) => dataToSend.append("completion_times[]", time));
  
      Object.entries(formData.quantity).forEach(([roomId, count]) => {
        dataToSend.append(`quantity[${roomId}]`, count);
      });
  
      Object.entries(formData.product_quantity).forEach(([productId, product]) => {
        Object.entries(product).forEach(([field, value]) => {
          dataToSend.append(`product_quantity[${productId}][${field}]`, value);
        });
      });
  
      return dataToSend;
    };
  
    const handleUpdateProperty = async () => {
      try {
        const dataToSend = prepareFormData(1);
        const response = await propertiesService.updateProperty(dataToSend);
        if (response.status) {
          notification.success({
            message: 'Success',
            description: 'Property updated successfully!'
          });
          onUpdate(propertyId, formData.product_quantity);
          onClose();
        } else {
          notification.error({
            message: 'Error',
            description: response.message || 'Failed to update property'
          });
        }
      } catch (error) {
        console.error("Error updating property:", error);
        notification.error({
          message: 'Error',
          description: 'An error occurred while updating the property'
        });
      }
    };
  
    const handleUpdateAndAddToCart = async () => {
      try {
        const dataToSend = prepareFormData(2);
        const response = await propertiesService.updateProperty(dataToSend);
        if (response.status) {
          notification.success({
            message: 'Success',
            description: 'Property updated and added to cart successfully!'
          });
          
          // Emit cart update event
          if (response.cart) {
            eventBus.emit('cart:updated', response.cart);
          }
          
          onUpdate(propertyId, formData.product_quantity);
          onClose();
        } else {
          notification.error({
            message: 'Error',
            description: response.message || 'Failed to update property and add to cart'
          });
        }
      } catch (error) {
        console.error("Error updating property and adding to cart:", error);
        notification.error({
          message: 'Error',
          description: 'An error occurred while updating the property and adding to cart'
        });
      }
    };
  
    const handleAddToCartOnly = async () => {
      try {
        const dataToSend = prepareFormData(3);
        const response = await propertiesService.updateProperty(dataToSend);
        if (response.status) {
          notification.success({
            message: 'Success',
            description: 'Property added to cart successfully!'
          });
          
          // Emit cart update event
          if (response.cart) {
            eventBus.emit('cart:updated', response.cart);
          }
          
          onUpdate(propertyId, formData.product_quantity);
          onClose();
        } else {
          notification.error({
            message: 'Error',
            description: response.message || 'Failed to add property to cart'
          });
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        notification.error({
          message: 'Error',
          description: 'An error occurred while adding to cart'
        });
      }
    };

    const handleAddExtraItem = (extraItem) => {
      setFormData((prevData) => ({
        ...prevData,
        product_quantity: {
          ...prevData.product_quantity,
          [extraItem.id]: {
            id: extraItem.id,
            name: extraItem.name,
            qty: extraItem.qty,
            price: extraItem.price,
            isExtra: true,
            type: "extra",
          },
        },
      }));
    };

    const handleRemoveExtraItem = (extraItemId) => {
      setFormData((prevData) => {
        const nextProductQuantity = { ...prevData.product_quantity };
        delete nextProductQuantity[extraItemId];
        return { ...prevData, product_quantity: nextProductQuantity };
      });
    };
  
    return (
      <Dialog open={isOpen} onClose={onClose}>
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Edit Cart Items</h2>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
  
            <DefaultOrder
              categories={categories}
              onAddExtraItem={handleAddExtraItem}
              onRemoveExtraItem={handleRemoveExtraItem}
              products={products}
              packs={packs}
              defaultOrder={formData.product_quantity}
              onQuantityChange={handleQuantityChange}
              onRentTypeChange={handleRentTypeChange}
            />
  
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProperty}
                className="px-4 py-2 bg-golden-navy text-white rounded-lg hover:bg-opacity-90"
              >
                Update Property
              </button>
              <button
                onClick={handleUpdateAndAddToCart}
                className="px-4 py-2 bg-golden-navy text-white rounded-lg hover:bg-opacity-90"
              >
                Update Property and Add to Cart
              </button>
              <button
                onClick={handleAddToCartOnly}
                className="px-4 py-2 border border-golden-navy text-golden-navy rounded-lg hover:bg-golden-navy hover:text-white"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    );
  };

export default EditCartModal;
